"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useRouter } from "next/navigation";
import type { FormDefinition, FormField } from "@/features/forms/types";
import { useMcpTool, text } from "./useMcpTool";
import { resolveNavPath } from "./paths";
type SubmitResult = {
  success: boolean;
  message?: string;
};

// Canonical top-level sections, shown to the agent in the navigate tool's
// description. Detail pages live under these with a slug (e.g. /cases/<slug>).
const SECTIONS: ReadonlyArray<readonly [string, string]> = [["/", "Startseite / Home"], ["/leistungen", "Leistungen / Services"], ["/branchen", "Branchen / Industries"], ["/cases", "Cases / Referenzen"], ["/karriere", "Karriere / Jobs"], ["/career-stories", "Career Stories"], ["/news", "News Hub"], ["/events", "Events"], ["/ueber-muuuh", "Über MUUUH!"], ["/kontakt", "Kontakt / Contact"]];

/** Always-on tool: navigate the browser to any page on the site. */
export function useNavigationTool() {
  const $ = _c(8);
  const router = useRouter();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname?.split("/").filter(Boolean)[0] || "de";
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const locale = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = SECTIONS.map(_temp).join("\n");
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      type: "object",
      properties: {
        path: {
          type: "string",
          description: "Internal path like '/leistungen' or '/cases/some-slug', or a full https URL."
        }
      },
      required: ["path"]
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== locale || $[5] !== router) {
    t3 = {
      name: "navigate",
      description: "Navigate the browser to a page on the MUUUH! website. Pass an internal path starting with '/'. Main sections:\n" + t1 + "\nDetail pages use a slug, e.g. '/cases/<slug>' or '/news/<slug>'. " + "Absolute http(s) URLs open in a new tab.",
      inputSchema: t2,
      execute: t4 => {
        const {
          path
        } = t4;
        const target = String(path ?? "").trim();
        if (!target) {
          return text("No path provided.", true);
        }
        const {
          external,
          href
        } = resolveNavPath(target, locale);
        if (external) {
          window.open(href, "_blank", "noopener,noreferrer");
          return text(`Opened external URL in a new tab: ${href}`);
        }
        router.push(href);
        return text(`Navigating to ${href}.`);
      }
    };
    $[4] = locale;
    $[5] = router;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useMcpTool(t3, t4);
}

/**
 * Newsletter subscription tool. `subscribe` should perform the actual submission
 * (injected by the component that owns the newsletter form, so field-building and
 * legal-consent handling are reused, not duplicated).
 */
function _temp(t0) {
  const [p, l] = t0;
  return `- ${p} — ${l}`;
}
export function useNewsletterTool(subscribe) {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      type: "object",
      properties: {
        email: {
          type: "string",
          format: "email",
          description: "Subscriber email address."
        }
      },
      required: ["email"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== subscribe) {
    t1 = {
      name: "subscribe_newsletter",
      description: "Subscribe an email address to the MUUUH! newsletter (MUUUH! Insights).",
      inputSchema: t0,
      execute: async t2 => {
        const {
          email
        } = t2;
        const value = String(email ?? "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return text("Please provide a valid email address.", true);
        }
        const res = await subscribe(value);
        return res.success ? text(`Subscribed ${value} to the newsletter.`) : text(res.message || "Newsletter subscription failed.", true);
      }
    };
    $[1] = subscribe;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useMcpTool(t1, t2);
}
const fieldToSchema = (f: FormField): Record<string, unknown> => {
  switch (f.type) {
    case "number":
      return {
        type: "number",
        description: f.label
      };
    case "checkbox":
    case "boolean":
      return {
        type: "boolean",
        description: f.label
      };
    case "select":
      return {
        type: "string",
        description: f.label,
        ...(f.options?.length ? {
          enum: f.options.map(o => o.value)
        } : {})
      };
    default:
      return {
        type: "string",
        description: f.label
      };
  }
};

/** JSON Schema `{ properties, required }` derived from a form's fields. */
const buildFormSchema = (formDefinition: FormDefinition) => {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  for (const f of formDefinition.fields) {
    // An agent cannot supply a file, and a string here would be submitted as
    // if it were an uploaded file's URL.
    if (f.type === "file") continue;
    properties[f.name] = fieldToSchema(f);
    if (f.required) required.push(f.name);
  }
  return {
    type: "object",
    properties,
    required
  };
};

/**
 * Exposes the HubSpot form on the current page as a tool. Input schema is derived
 * from the form definition; `submit` runs the existing submission flow.
 */
export function useHubspotFormTool(t0) {
  const $ = _c(14);
  const {
    formDefinition,
    submit,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const t2 = `submit_form_${formDefinition.id}`;
  let t3;
  if ($[0] !== formDefinition.fields) {
    t3 = formDefinition.fields.filter(_temp2).map(_temp3).join(", ") || "none";
    $[0] = formDefinition.fields;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `Submit the "${formDefinition.name}" form on the current page. ` + `Fields: ${t3}.`;
  let t5;
  if ($[2] !== formDefinition) {
    t5 = buildFormSchema(formDefinition);
    $[2] = formDefinition;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== formDefinition.name || $[5] !== submit) {
    t6 = async data => {
      const res = await submit(data);
      return res.success ? text(`Submitted "${formDefinition.name}".`) : text(res.message || "Form submission failed.", true);
    };
    $[4] = formDefinition.name;
    $[5] = submit;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t2 || $[8] !== t4 || $[9] !== t5 || $[10] !== t6) {
    t7 = {
      name: t2,
      description: t4,
      inputSchema: t5,
      execute: t6
    };
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== formDefinition.id) {
    t8 = [formDefinition.id];
    $[12] = formDefinition.id;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  useMcpTool(t7, t8, enabled);
}

/**
 * Contact-person tool for person pages. Reuses the page's contact form + submission
 * flow, but presents as `contact_person` and tags the message with which person the
 * request concerns (so the recipient knows the target). `person` is required when
 * `enabled` is true.
 */
function _temp3(f_0) {
  return f_0.name;
}
function _temp2(f) {
  return f.type !== "file";
}
export function useContactPersonTool(t0) {
  const $ = _c(18);
  const {
    person,
    formDefinition,
    submit,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const name = person?.name ?? "";
  const role = person?.jobtitle ? ` (${person.jobtitle})` : "";
  let t2;
  if ($[0] !== formDefinition.fields) {
    t2 = formDefinition.fields.some(_temp4);
    $[0] = formDefinition.fields;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hasMessage = t2;
  let t3;
  if ($[2] !== formDefinition.fields) {
    t3 = formDefinition.fields.map(_temp5).join(", ");
    $[2] = formDefinition.fields;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `Send a contact request to ${name}${role} via the contact form on this page. ` + "Provide the sender's own details; the request is tagged as concerning " + `${name}. Fields: ${t3}.`;
  let t5;
  if ($[4] !== formDefinition) {
    t5 = buildFormSchema(formDefinition);
    $[4] = formDefinition;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== hasMessage || $[7] !== name || $[8] !== role || $[9] !== submit) {
    t6 = async data => {
      const payload = {
        ...data
      };
      if (hasMessage) {
        const note = `Diese Kontaktanfrage richtet sich an: ${name}${role}.`;
        payload.message = payload.message ? `${note}\n\n${payload.message}` : note;
      }
      const res = await submit(payload);
      return res.success ? text(`Contact request to ${name} sent.`) : text(res.message || "Contact request failed.", true);
    };
    $[6] = hasMessage;
    $[7] = name;
    $[8] = role;
    $[9] = submit;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = {
      name: "contact_person",
      description: t4,
      inputSchema: t5,
      execute: t6
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== formDefinition.id || $[16] !== name) {
    t8 = [formDefinition.id, name];
    $[15] = formDefinition.id;
    $[16] = name;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  useMcpTool(t7, t8, enabled);
}
function _temp5(f_0) {
  return f_0.name;
}
function _temp4(f) {
  return f.name === "message";
}
type ContactPersonInfo = {
  name: string;
  jobtitle?: string | null;
  slug: string;
};

/** Always-on tool: list all MUUUH! contact persons and their contact-page paths. */
export function useContactPersonsListTool() {
  const $ = _c(2);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      name: "list_contact_persons",
      description: "List all MUUUH! contact persons (team members you can reach out to). Returns each person's name, role and the path to their contact page. Navigate there with the `navigate` tool, then use `contact_person` to send a request.",
      inputSchema: {
        type: "object",
        properties: {}
      },
      execute: _temp7
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useMcpTool(t0, t1);
}
async function _temp7() {
  const res = await fetch("/api/contact-persons");
  if (!res.ok) {
    return text("Could not load contact persons.", true);
  }
  const {
    persons
  } = (await res.json()) as {
    persons: ContactPersonInfo[];
  };
  if (!persons?.length) {
    return text("No contact persons found.");
  }
  const lines = persons.map(_temp6);
  return text(`Contact persons:\n${lines.join("\n")}`);
}
function _temp6(p) {
  return `- ${p.name}${p.jobtitle ? ` — ${p.jobtitle}` : ""} (/de/kontakt/${p.slug})`;
}